import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { isValidId } from '../../u2000Core/tools/U2000IdUtils';
import {
  doCreateData,
  luHandleAppiError as u3141LuHandleAppiError,
  u3141MasterLotChainToApiPayload,
  u3141ParseMasterLotChainFromApiResult,
  U3141EmptyLandRegister,
  type U3141CreatePrefillState,
} from '../../u3141LandRegister/U3141Service';
import { NS as U3141_NS } from '../../u3141LandRegister/U3141';
import { NS } from '../U3121';
import { luHandleAppiError, type U3121LandRegisterCoverage, type U3121LandRegisterCoverageLot } from '../U3121Service';
import { useU3121LandRegisterCoverageHandlers } from '../U3121UseLandRegisterCoverageHandlers';
import U3121CreateLandRegisterLotsDialog from './U3121CreateLandRegisterLotsDialog';

interface U3121CreateLandRegisterButtonProps {
  projectId: string;
  subProjectId?: string;
  enabled: boolean;
}

function U3121CreateLandRegisterButton({ projectId, subProjectId, enabled }: U3121CreateLandRegisterButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const axios = useU2000AxiosAuthenticated();
  const toast = useToast();
  const { getCoverage, getSubProjectCoverage } = useU3121LandRegisterCoverageHandlers();
  const [coverage, setCoverage] = useState<U3121LandRegisterCoverage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const loadCoverage = useCallback(async () => {
    if (!enabled || !projectId || !isValidId(projectId)) {
      setCoverage(null);
      return;
    }
    setIsLoading(true);
    try {
      const result = subProjectId && isValidId(subProjectId)
        ? await getSubProjectCoverage(projectId, subProjectId)
        : await getCoverage(projectId);
      setCoverage(result);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, projectId, subProjectId, getCoverage, getSubProjectCoverage]);

  useEffect(() => {
    void loadCoverage();
  }, [loadCoverage, location.key]);

  const pendingLots: U3121LandRegisterCoverageLot[] = useMemo(
    () => (coverage?.lots ?? []).filter((entry) => !entry.existsInRegister),
    [coverage],
  );

  const goCreateLandRegister = useCallback(
    async (requestedLot: string) => {
      if (!coverage || isCreating) return;
      const lot = requestedLot?.trim();
      if (!lot) return;

      const prefill: U3141CreatePrefillState = {
        projectNumber: coverage.projectNumber,
        projectDescription: coverage.projectDescription,
        requestedLot: lot,
      };

      setIsCreating(true);
      try {
        const payload = u3141MasterLotChainToApiPayload({
          ...U3141EmptyLandRegister,
          ...prefill,
        });
        const response = await doCreateData(axios, payload);
        const created = u3141ParseMasterLotChainFromApiResult(response.data);
        if (!created?.id || !isValidId(created.id)) {
          toast.error(t('msgCreateLandRegisterFailed', { ns: NS }), t('errorTitle', { ns: NS }));
          return;
        }

        setIsDialogOpen(false);
        navigate(`/U3141/${created.id}`, {
          state: {
            openLotChainGraph: true,
            u3141ActionOriginFrom: location.pathname,
          },
        });
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, U3141_NS, u3141LuHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        } else {
          const fallback = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
          fallback.genericErrors?.forEach((err) => toast.error(err.text, err.title));
        }
      } finally {
        setIsCreating(false);
      }
    },
    [axios, coverage, isCreating, location.pathname, navigate, t, toast],
  );

  const handleClick = () => {
    if (isCreating) return;
    if (pendingLots.length === 1) {
      void goCreateLandRegister(pendingLots[0].lot);
    } else if (pendingLots.length > 1) {
      setIsDialogOpen(true);
    }
  };

  if (!enabled) return null;

  const isDisabled = isLoading || isCreating || pendingLots.length === 0;
  const title = isDisabled && !isLoading && !isCreating ? t('tooltipCreateLandRegisterNoPending', { ns: NS }) : undefined;

  return (
    <>
      <U3121CreateLandRegisterLotsDialog
        isOpen={isDialogOpen}
        pendingLots={pendingLots}
        isCreating={isCreating}
        onClose={() => {
          if (!isCreating) setIsDialogOpen(false);
        }}
        onCreate={(lot) => void goCreateLandRegister(lot)}
      />
      <button
        type="button"
        className="shrink-0 rounded-lg border border-primary-600 bg-white px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400"
        disabled={isDisabled}
        title={title}
        onClick={handleClick}
      >
        {t('lblCreateLandRegister', { ns: NS })}
      </button>
    </>
  );
}

export default U3121CreateLandRegisterButton;
